import classNames from 'classnames'
import {useEffect, memo} from 'react'

import OptimizedTime from '../OptimizedTime/OptimizedTime'
import {useRunDuration, useRunRemainingTime} from '../TimeBadge.hooks'

import styles from './TimeBadge.css'

import {subscribeOnBuild} from 'src/actions/builds'
import Badge from 'src/components/common/Badge/Badge'
import BuildQueueInfo from 'src/components/common/BuildQueueInfo/BuildQueueInfo.container'
import SvgIcon from 'src/components/common/SvgIcon/SvgIcon'
import {useAppSelector, useAppDispatch} from 'src/hooks/react-redux'
import {getBuild} from 'src/selectors'
import {formatDuration} from 'src/utils/dateTimeUtils'

type Props = {
  runId?: number
  className?: string
  durationLabel?: string
  withOptimizedTime?: boolean
}
function TimeBadge({className, runId, withOptimizedTime, durationLabel = 'Duration'}: Props) {
  const dispatch = useAppDispatch()

  const runState = useAppSelector(state => getBuild(state, runId)?.state)
  const isRunning = runState === 'running'
  const isQueued = runState === 'queued'

  const duration = useRunDuration(runId)
  const timeLeft = useRunRemainingTime(runId)

  useEffect(() => {
    if (runId != null && isRunning) {
      return dispatch(subscribeOnBuild(runId))
    }
    return undefined
  }, [runId, isRunning, dispatch])

  return (
    <Badge className={classNames(styles.container, className)}>
      {!isQueued && (
        <div>
          <div className={styles.label}>{isRunning ? 'Running time' : durationLabel}</div>
          <div className={styles.time}>
            <SvgIcon className={styles.icon} icon={'hourglass'} />
            {formatDuration(duration)}
          </div>
        </div>
      )}
      {isRunning && (
        <div>
          <div className={styles.label}>{'Left'}</div>
          <div className={styles.time}>
            <SvgIcon className={styles.icon} icon={'hourglass'} />
            {formatDuration(timeLeft)}
          </div>
        </div>
      )}
      {runId && !isRunning && (
        <div>
          <div className={styles.label}>{'In queue'}</div>
          <BuildQueueInfo
            titleClassName={styles.queueInfoTitle}
            buildId={runId}
            withoutTitle
            withIcons
          />
        </div>
      )}
      {withOptimizedTime && <OptimizedTime />}
    </Badge>
  )
}

export default memo(TimeBadge)
